import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoadPanel, SelectBox, TextBox, NumberBox } from "devextreme-react";
import Button from "devextreme-react/button";
import HtmlEditor, { Item, Toolbar as HTMLToolbar } from "devextreme-react/html-editor";
import notify from "devextreme/ui/notify";
import {
    TicketPriority, TICKET_STATUS_DEFS, createTicket, getProducts, getSupportUsers, getVendors, getSupportGroups,
    Product, SupportUser, Vendor, SupportGroup,
} from "../../../api/support";
import { MOCK_VENDORS } from "../vendors/vendor-mock";
import { MOCK_SUPPORT_GROUPS } from "../support-group-mock";

// ─── 6502 / UpdateOrInsertTicket alan secenekleri ─────────────────────────────

// ticketTypeNr — Support=1, Bug=2, Feature=3, Service=4
const TICKET_TYPE_OPTIONS = [
    { value: 1, labelKey: "supportRequest" },
    { value: 2, labelKey: "bugRequest" },
    { value: 3, labelKey: "featureRequest" },
    { value: 4, labelKey: "serviceRequest" },
];

// contactChannelRef — AT_ContactChannels
const CONTACT_CHANNELS = [
    { id: 1, label: "Telefon" },
    { id: 2, label: "E-posta" },
    { id: 3, label: "Yuz Yuze" },
    { id: 4, label: "Uzak Baglanti" },
    { id: 5, label: "Portal" },
];

// ticketPriorityNr — 1=high, 2=medium, 3=low
const PRIORITY_OPTIONS: { value: TicketPriority; label: string; color: string }[] = [
    { value: 1, label: "Yuksek", color: "#EF4444" },
    { value: 2, label: "Normal", color: "#F59E0B" },
    { value: 3, label: "Dusuk", color: "#9CA3AF" },
];

const PRODUCT_CODES = [
    { code: 10, name: "mobiOffice" },
    { code: 11, name: "mobiSales" },
    { code: 12, name: "mobiShop" },
    { code: 14, name: "mobiRoute" },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface NewTicketPageProps {
    // Splitter altinda bir tab icinde gomulu acildiginda kullanilir.
    // Verilmezse bilesen kendi route'unda standalone calisir (navigate ile).
    embedded?: boolean;
    onCancel?: () => void;
    onCreated?: () => void;
    // Secili vendor'in bilgi tab'ini acmak icin (TicketPage saglar).
    onOpenVendor?: (vendorRef: string, vendorName?: string) => void;
}

export default function NewTicketPage({ embedded, onCancel, onCreated, onOpenVendor }: NewTicketPageProps = {}) {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState<Product[]>([]);
    const [supportUsers, setSupportUsers] = useState<SupportUser[]>([]);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [supportGroups, setSupportGroups] = useState<SupportGroup[]>([]);

    // Form alanlari — 6502 parametreleriyle birebir
    const [ticketStatusNr, setTicketStatusNr] = useState<number>(1);   // varsayilan NEW
    const [ticketTypeNr, setTicketTypeNr] = useState<number>(1);
    const [ticketTitle, setTicketTitle] = useState("");
    const [ticketDescription, setTicketDescription] = useState("");
    const [vendorRef, setVendorRef] = useState("");
    const [contactRef, setContactRef] = useState("");
    const [contactChannelRef, setContactChannelRef] = useState<number>(1);
    const [supportGroupRef, setSupportGroupRef] = useState("");
    const [supportProductRef, setSupportProductRef] = useState<number | null>(null);
    const [ticketPriorityNr, setTicketPriorityNr] = useState<TicketPriority>(2);
    const [assignedToRef, setAssignedToRef] = useState("");

    const statusOptions = TICKET_STATUS_DEFS.filter(d => d.isActive);

    useEffect(() => {
        Promise.all([getProducts(), getSupportUsers(), getVendors(), getSupportGroups()])
            .then(([p, u, v, g]) => { setProducts(p); setSupportUsers(u); setVendors(v); setSupportGroups(g); })
            .catch(() => notify("Veriler yuklenemedi", "error", 2000));
    }, []);

    // Backend bos donerse mock fallback (urun SelectBox'taki paternin aynisi).
    const vendorSource = vendors.length ? vendors : MOCK_VENDORS;
    const supportGroupSource = supportGroups.length ? supportGroups : MOCK_SUPPORT_GROUPS;

    const isValid = !!(ticketTitle.trim() && supportProductRef !== null && vendorRef.trim() && supportGroupRef.trim());

    // Gomulu modda tab'i kapat (onCancel); standalone'da ticket listesine don.
    const cancel = () => (onCancel ? onCancel() : navigate("/support/tickets"));

    const handleSubmit = async () => {
        if (!isValid) {
            notify("Lutfen zorunlu alanlari doldurun", "warning", 2500);
            return;
        }
        setLoading(true);
        try {
            await createTicket({
                status: ticketStatusNr as any,
                ticket_type: ticketTypeNr,
                title: ticketTitle.trim(),
                description: ticketDescription,
                vendor_ref: vendorRef,
                contact_ref: contactRef ? Number(contactRef) : undefined,
                contact_channel: contactChannelRef,
                support_group: supportGroupRef ? Number(supportGroupRef) : undefined,
                product_code: supportProductRef!,
                priority: ticketPriorityNr,
                assigned_to: assignedToRef || undefined,
                assigned_to_name: supportUsers.find(u => u.ref === assignedToRef)?.full_name,
            });
            notify("Ticket olusturuldu", "success", 2000);
            // Gomulu modda tab'i kapat + listeyi yenile (onCreated); standalone'da listeye don.
            if (onCreated) onCreated();
            else navigate("/support/tickets");
        } catch {
            notify("Ticket olusturulamadi", "error", 2000);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-full w-full" style={{ background: 'var(--background-color, #f5f5f5)', overflow: 'hidden' }}>
            <LoadPanel visible={loading} />

            {/* Header */}
            <div
                className="flex items-center gap-2 shrink-0"
                style={{ height: 52, padding: '0 16px', background: 'var(--base-bg)', borderBottom: '1px solid var(--border-color)' }}
            >
                {!embedded && <Button icon="chevronleft" stylingMode="text" hint="Geri" onClick={cancel} />}
                <span className="text-sm font-semibold flex-1" style={{ color: 'var(--base-text-color)' }}>
                    {t("new-ticket")}
                </span>
                <Button text="Iptal" stylingMode="outlined" onClick={cancel} />
                <Button text="Olustur" icon="check" type="default" stylingMode="contained" disabled={!isValid || loading} onClick={handleSubmit} />
            </div>

            {/* Body - standalone: tek ekrana sigar; embedded: alt panele sigsin diye tum form kayar */}
            <div className="flex-1 min-h-0" style={{ padding: embedded ? 10 : 14, overflowY: embedded ? 'auto' : undefined }}>
                <div
                    className={`grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-0 ${embedded ? "" : "h-full"}`}
                    style={{ maxWidth: embedded ? undefined : 1200, margin: embedded ? undefined : '0 auto' }}
                >
                    {/* Sol - Talep Detayi (standalone'da yuksekligi doldurur) */}
                    <div className="lg:col-span-2 min-h-0">
                        <Card title="Talep Detayi" fill={!embedded}>
                            <Field label="Baslik" required>
                                <TextBox
                                    value={ticketTitle}
                                    onValueChanged={e => setTicketTitle(e.value ?? "")}
                                    placeholder="Ticket basligi"
                                    maxLength={251}
                                    stylingMode="outlined"
                                    width="100%"
                                />
                            </Field>
                            <Field label="Aciklama" fill={!embedded}>
                                <div className="flex-1 min-h-0" style={{ border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden', height: embedded ? 200 : undefined }}>
                                    <HtmlEditor
                                        height="100%"
                                        value={ticketDescription}
                                        onValueChanged={e => setTicketDescription(e.value)}
                                        placeholder="Sorunu detayli anlatiniz..."
                                    >
                                        <HTMLToolbar>
                                            <Item name="undo" /><Item name="redo" /><Item name="separator" />
                                            <Item name="bold" /><Item name="italic" /><Item name="underline" />
                                            <Item name="separator" />
                                            <Item name="orderedList" /><Item name="bulletList" />
                                            <Item name="separator" /><Item name="link" />
                                        </HTMLToolbar>
                                    </HtmlEditor>
                                </div>
                            </Field>
                        </Card>
                    </div>

                    {/* Sag - Meta (kompakt, 2'li grid). Embedded'da tum body kaydigi icin ic scroll yok. */}
                    <div className="min-h-0 flex flex-col gap-4" style={{ overflowY: embedded ? undefined : 'auto' }}>
                        <Card title="Siniflandirma">
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Durum">
                                    <SelectBox
                                        dataSource={statusOptions}
                                        value={ticketStatusNr}
                                        valueExpr="nr"
                                        displayExpr={(d: any) => d ? t(d.labelKey) : ""}
                                        onValueChanged={e => setTicketStatusNr(Number(e.value))}
                                        stylingMode="outlined"
                                        width="100%"
                                    />
                                </Field>
                                <Field label="Tip">
                                    <SelectBox
                                        dataSource={TICKET_TYPE_OPTIONS}
                                        value={ticketTypeNr}
                                        valueExpr="value"
                                        displayExpr={(o: any) => o ? t(o.labelKey) : ""}
                                        onValueChanged={e => setTicketTypeNr(Number(e.value))}
                                        stylingMode="outlined"
                                        width="100%"
                                    />
                                </Field>
                            </div>
                            <Field label="Oncelik">
                                <div className="flex gap-2">
                                    {PRIORITY_OPTIONS.map(p => {
                                        const active = ticketPriorityNr === p.value;
                                        return (
                                            <button
                                                key={p.value}
                                                type="button"
                                                onClick={() => setTicketPriorityNr(p.value)}
                                                className="flex-1 flex items-center justify-center gap-1.5 rounded-md text-xs font-medium transition-all"
                                                style={{
                                                    height: 32,
                                                    border: `1px solid ${active ? p.color : 'var(--border-color)'}`,
                                                    background: active ? `${p.color}1a` : 'transparent',
                                                    color: active ? p.color : 'var(--subtitle-text-color)',
                                                }}
                                            >
                                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
                                                {p.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </Field>
                        </Card>

                        <Card title="Musteri & Kanal">
                            <Field label={t("vendorLabel")} required>
                                <div className="flex gap-2">
                                    <div className="flex-1 min-w-0">
                                        <SelectBox
                                            dataSource={vendorSource}
                                            value={vendorRef ? Number(vendorRef) : null}
                                            valueExpr="ref"
                                            displayExpr="name"
                                            onValueChanged={e => setVendorRef(e.value != null ? String(e.value) : "")}
                                            placeholder={t("vendorSelectPlaceholder")}
                                            stylingMode="outlined"
                                            width="100%"
                                            searchEnabled
                                            showClearButton
                                        />
                                    </div>
                                    {/* Secili vendor'in bilgi tab'ini ac (yalnizca gomulu modda anlamli) */}
                                    <Button
                                        icon="info"
                                        stylingMode="outlined"
                                        hint={t("vendorInfo")}
                                        disabled={!vendorRef || !onOpenVendor}
                                        onClick={() => {
                                            const v = vendorSource.find(x => String(x.ref) === vendorRef);
                                            onOpenVendor?.(vendorRef, v?.name);
                                        }}
                                    />
                                </div>
                            </Field>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Contact Ref">
                                    <NumberBox
                                        value={contactRef ? Number(contactRef) : undefined}
                                        onValueChanged={e => setContactRef(e.value != null ? String(e.value) : "")}
                                        placeholder="0"
                                        stylingMode="outlined"
                                        width="100%"
                                        showSpinButtons={false}
                                    />
                                </Field>
                                <Field label="Iletisim Kanali">
                                    <SelectBox
                                        dataSource={CONTACT_CHANNELS}
                                        value={contactChannelRef}
                                        valueExpr="id"
                                        displayExpr="label"
                                        onValueChanged={e => setContactChannelRef(Number(e.value))}
                                        stylingMode="outlined"
                                        width="100%"
                                    />
                                </Field>
                            </div>
                        </Card>

                        <Card title="Atama & Urun">
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Urun" required>
                                    <SelectBox
                                        dataSource={products.length ? products : PRODUCT_CODES}
                                        value={supportProductRef}
                                        valueExpr="code"
                                        displayExpr="name"
                                        onValueChanged={e => setSupportProductRef(e.value ?? null)}
                                        placeholder="Secin..."
                                        stylingMode="outlined"
                                        width="100%"
                                        showClearButton
                                    />
                                </Field>
                                <Field label={t("supportGroup")} required>
                                    <SelectBox
                                        dataSource={supportGroupSource}
                                        value={supportGroupRef ? Number(supportGroupRef) : null}
                                        valueExpr="ref"
                                        displayExpr="name"
                                        onValueChanged={e => setSupportGroupRef(e.value != null ? String(e.value) : "")}
                                        placeholder={t("selectSupportGroup")}
                                        stylingMode="outlined"
                                        width="100%"
                                        searchEnabled
                                        showClearButton
                                    />
                                </Field>
                            </div>
                            <Field label="Atanacak Personel">
                                <SelectBox
                                    dataSource={supportUsers}
                                    value={assignedToRef}
                                    valueExpr="ref"
                                    displayExpr="full_name"
                                    onValueChanged={e => setAssignedToRef(e.value ?? "")}
                                    placeholder="Personel secin..."
                                    stylingMode="outlined"
                                    width="100%"
                                    showClearButton
                                    searchEnabled
                                />
                            </Field>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Yardimci bilesenler ──────────────────────────────────────────────────────

function Card({ title, children, fill }: { title: string; children: React.ReactNode; fill?: boolean }) {
    return (
        <div
            className={fill ? "h-full flex flex-col min-h-0" : ""}
            style={{ background: 'var(--base-bg)', border: '1px solid var(--border-color)', borderRadius: 10, overflow: 'hidden' }}
        >
            <div
                className="text-xs font-semibold uppercase shrink-0"
                style={{ padding: '9px 16px', letterSpacing: '0.04em', color: 'var(--subtitle-text-color)', borderBottom: '1px solid var(--border-color)' }}
            >
                {title}
            </div>
            <div className={`flex flex-col ${fill ? "flex-1 min-h-0" : ""}`} style={{ gap: 12, padding: 14 }}>
                {children}
            </div>
        </div>
    );
}

function Field({ label, required, children, fill }: { label: string; required?: boolean; children: React.ReactNode; fill?: boolean }) {
    return (
        <div className={fill ? "flex-1 min-h-0 flex flex-col" : ""}>
            <label className="block text-xs shrink-0" style={{ marginBottom: 5, color: 'var(--subtitle-text-color)' }}>
                {label}
                {required && <span style={{ color: 'var(--error-color, #d13438)', marginLeft: 2 }}>*</span>}
            </label>
            {fill ? <div className="flex-1 min-h-0 flex flex-col">{children}</div> : children}
        </div>
    );
}
